'use client';

import { Brain, ArrowsClockwise, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ai-elements/skeleton';

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface ProfileCardProps {
  profileText: string | null;
  updatedAt: string | null;
  loading: boolean;
  rebuilding: boolean;
  onRebuild: () => void;
  /** Only the Personal scope has a synthesized profile. */
  isUserScope: boolean;
}

export function ProfileCard({
  profileText,
  updatedAt,
  loading,
  rebuilding,
  onRebuild,
  isUserScope,
}: ProfileCardProps) {
  if (!isUserScope) return null;

  return (
    <section className="rounded-2xl border border-border/70 bg-card/40 p-[18px]">
      <div className="flex items-start gap-3">
        <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-violet-500/12">
          <Brain weight="duotone" className="size-5 text-violet-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-extrabold tracking-tight">Profile digest</h2>
            {updatedAt && (
              <span className="text-[11px] text-muted-foreground">· updated {formatWhen(updatedAt)}</span>
            )}
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            A synthesized summary of your stable preferences, kept always-on in context.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8 shrink-0 rounded-full px-3 text-xs font-semibold"
          onClick={onRebuild}
          disabled={rebuilding || loading}
        >
          <ArrowsClockwise weight="bold" className={`mr-1.5 size-3.5 ${rebuilding ? 'animate-spin' : ''}`} />
          {rebuilding ? 'Rebuilding…' : 'Rebuild now'}
        </Button>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-11/12" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ) : profileText ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{profileText}</p>
        ) : (
          <div className="rounded-xl border border-dashed border-border/60 px-4 py-6 text-center">
            <Sparkle weight="duotone" className="mx-auto size-5 text-muted-foreground/60" />
            <p className="mt-2 text-sm font-medium">No profile yet</p>
            <p className="mx-auto mt-1 max-w-md text-[12px] leading-snug text-muted-foreground">
              Once you have a few durable facts, a nightly consolidation writes a compact
              profile here. You can also build it now with “Rebuild now.”
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
